// src/components/Portfolio.jsx
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import {
  FaReact,
  FaNodeJs,
  FaPython,
  FaDocker,
  FaAws,
  FaStream,
  FaBrain,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import {
  SiMongodb,
  SiPostgresql,
  SiSpringboot,
  SiRedis,
  SiKubernetes,
  SiMysql,
  SiExpress,
  SiGraphql,
  SiTensorflow,
  SiOpencv,
  SiStreamlit,
  SiLangchain,
} from "react-icons/si";

const techIcons = {
  React: <FaReact className="text-cyan-500" />,
  "Node.js": <FaNodeJs className="text-green-500" />,
  Express: <SiExpress className="text-slate-500" />,
  MongoDB: <SiMongodb className="text-green-500" />,
  PostgreSQL: <SiPostgresql className="text-blue-400" />,
  MySQL: <SiMysql className="text-sky-500" />,
  Redis: <SiRedis className="text-red-500" />,
  Kafka: <FaStream className="text-orange-500" />,
  Docker: <FaDocker className="text-blue-500" />,
  Kubernetes: <SiKubernetes className="text-blue-500" />,
  AWS: <FaAws className="text-orange-500" />,
  S3: <FaAws className="text-amber-500" />,
  "Spring Boot": <SiSpringboot className="text-green-500" />,
  GraphQL: <SiGraphql className="text-pink-500" />,
  Python: <FaPython className="text-yellow-500" />,
  TensorFlow: <SiTensorflow className="text-orange-500" />,
  OpenCV: <SiOpencv className="text-green-500" />,
  Streamlit: <SiStreamlit className="text-red-500" />,
  LangChain: <SiLangchain className="text-emerald-500" />,
  ChromaDB: <FaBrain className="text-amber-500" />,
  BERT: <FaBrain className="text-emerald-500" />,
};

/* Flagship projects are listed first and shown at full size.
   Practice builds are smaller, labeled exercises from AI/ML coursework. */
const projects = [
  {
    title: "AI Assistant with Memory & Tool Use",
    desc: "Conversational assistant built with LangGraph, ChromaDB for memory, and search tools layered on a production-style chat interface.",
    tech: ["Python", "Streamlit"],
    extra: ["LangChain", "ChromaDB", "AWS"],
    tags: ["AI/ML", "Full-Stack", "Cloud"],
  },
  {
    title: "Microservices Order Processing System",
    desc: "Event-driven order flow with resiliency, decoupled services, and scalable orchestration.",
    tech: ["Spring Boot", "Kafka", "PostgreSQL"],
    extra: ["Docker", "Kubernetes"],
    tags: ["Backend", "Cloud"],
  },
  {
    title: "Hospital Management Platform",
    desc: "Admin-driven workflows for patient records, appointments, and analytics.",
    tech: ["React", "Node.js", "MongoDB"],
    extra: ["Docker", "AWS"],
    tags: ["Full-Stack", "Cloud"],
  },
  {
    title: "Real-Time Blogging Platform",
    desc: "Scalable blogging platform with live updates, search, and user engagement.",
    tech: ["React", "Node.js", "MongoDB"],
    extra: ["Docker", "AWS"],
    tags: ["Full-Stack", "Cloud"],
  },
  {
    title: "Travel Booking Platform",
    desc: "Airbnb-style booking system with search, reservations, and real-time availability.",
    tech: ["Spring Boot", "MySQL", "React"],
    extra: ["Docker"],
    tags: ["Full-Stack"],
  },
  {
    title: "Real-Time Chat Application",
    desc: "Low-latency messaging app built with WebSockets and a responsive UI.",
    tech: ["React", "Node.js", "MongoDB"],
    extra: ["Docker", "AWS"],
    tags: ["Full-Stack"],
  },
  {
    title: "Distributed File Storage Service",
    desc: "Object storage system for secure upload, retrieval, and scalable storage operations.",
    tech: ["Node.js", "AWS", "S3"],
    extra: ["Docker"],
    tags: ["Backend", "Cloud"],
  },
  {
    title: "URL Shortener Service",
    desc: "Highly scalable link shortener with analytics and rate limiting.",
    tech: ["Node.js", "Redis", "PostgreSQL"],
    extra: ["Docker"],
    tags: ["Backend"],
  },
  {
    title: "AI-Powered Recommendation Engine",
    desc: "Graph-based recommendation model that suggests content and products.",
    tech: ["Python"],
    extra: ["Docker"],
    tags: ["AI/ML"],
  },
  {
    title: "Image & Video Captioning App",
    desc: "Interactive app combining image generation and caption generation workflows.",
    tech: ["Python", "Streamlit"],
    extra: [],
    tags: ["AI/ML", "Full-Stack"],
  },
  {
    title: "Kubernetes Deployment Platform",
    desc: "Production-ready Kubernetes deployments with monitoring and auto-scaling.",
    tech: ["Docker", "Kubernetes"],
    extra: ["AWS"],
    tags: ["Cloud"],
  },
  {
    title: "CI/CD Pipeline for Container Workloads",
    desc: "Automated build and deploy workflows for containerized microservices.",
    tech: ["Docker"],
    extra: ["AWS"],
    tags: ["Cloud"],
  },
  {
    title: "Handwritten Digit Generator (GAN)",
    desc: "Practice build: a GAN with a custom training loop that generates MNIST-style digits.",
    tech: ["Python", "TensorFlow"],
    extra: [],
    tags: ["AI/ML"],
    practice: true,
  },
  {
    title: "MNIST Autoencoder for Denoising",
    desc: "Practice build: a compressed-representation model for image denoising and reconstruction.",
    tech: ["Python", "TensorFlow"],
    extra: [],
    tags: ["AI/ML"],
    practice: true,
  },
  {
    title: "Variational Autoencoder — Face Generation",
    desc: "Practice build: a VAE trained on CelebA to generate realistic face samples.",
    tech: ["Python", "TensorFlow"],
    extra: [],
    tags: ["AI/ML"],
    practice: true,
  },
  {
    title: "BERT Sentiment Classifier",
    desc: "Practice build: fine-tuned BERT for sentiment classification with interpretability charts.",
    tech: ["Python", "BERT"],
    extra: ["TensorFlow"],
    tags: ["AI/ML"],
    practice: true,
  },
  {
    title: "LangChain Q&A App",
    desc: "Practice build: a retrieval-augmented question-answering interface.",
    tech: ["Python", "LangChain"],
    extra: [],
    tags: ["AI/ML"],
    practice: true,
  },
];

const ITEMS_PER_PAGE = 6;
const TABS = ["All", "AI/ML", "Full-Stack", "Backend", "Cloud"];

const Portfolio = () => {
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("All");
  const [query, setQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects = projects.filter((project) => {
    const matchesTab = activeTab === "All" || project.tags.includes(activeTab);
    const searchable = [project.title, project.desc, ...project.tech, ...project.extra, ...project.tags]
      .join(" ")
      .toLowerCase();

    return matchesTab && searchable.includes(query.trim().toLowerCase());
  });

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / ITEMS_PER_PAGE));
  const paginatedProjects = filteredProjects.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  return (
    <section name="Portfolio" className="aurora-section px-6 py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white md:text-4xl">
              <span className="accent-text">Portfolio</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 dark:text-zinc-400">
              Production-style systems alongside smaller AI/ML practice builds,
              clearly labeled so it's obvious which is which.
            </p>
          </div>
          <a
            href="https://github.com/AakashJha04?tab=repositories"
            target="_blank"
            rel="noreferrer"
            className="btn-secondary shrink-0"
          >
            View all on GitHub
          </a>
        </div>

        <div className="panel mb-8 grid gap-4 p-4 md:grid-cols-[1fr_auto] md:items-center">
          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Search by project, tech, or category"
              className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm font-medium text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
            />
          </div>

          <div className="flex flex-wrap justify-center gap-2 md:justify-end">
            {TABS.map((tab) => (
              <button
                key={tab}
                className={`pill-button ${activeTab === tab ? "pill-button-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  setPage(1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeTab}-${page}-${query}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3"
          >
            {paginatedProjects.map((p, i) => (
              <div key={p.title} className="panel flex flex-col p-6">
                <div className="mb-3 flex items-center justify-between gap-3">
                  {p.practice ? (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500 dark:bg-zinc-800 dark:text-zinc-400">
                      Practice build
                    </span>
                  ) : (
                    <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
                      Project
                    </span>
                  )}
                  <span className="text-xs font-medium text-slate-400">{p.tags.join(" / ")}</span>
                </div>

                <h3 className="mb-2 text-base font-bold text-slate-900 dark:text-white">{p.title}</h3>
                <p className="mb-4 flex-1 text-sm leading-6 text-slate-600 dark:text-zinc-400">{p.desc}</p>

                <div className="mb-5 flex flex-wrap gap-2">
                  {[...p.tech, ...p.extra].map((tech) => (
                    <span
                      key={tech}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                      {techIcons[tech] || <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />}
                      {tech}
                    </span>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedProject(p)}
                  className="btn-secondary mt-auto w-full"
                >
                  View Details
                </button>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {paginatedProjects.length === 0 && (
          <div className="panel p-8 text-center text-slate-600 dark:text-zinc-400">
            No projects match that search yet.
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="pill-button disabled:cursor-not-allowed disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-sm font-medium text-slate-600 dark:text-zinc-400">
            Page {page} of {totalPages} · {filteredProjects.length} projects
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="pill-button disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-950/50 px-4 py-10"
            onMouseDown={() => setSelectedProject(null)}
          >
            <motion.article
              initial={{ opacity: 0, y: 16, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.97 }}
              transition={{ duration: 0.18 }}
              className="panel relative max-h-[88vh] w-full max-w-2xl overflow-y-auto p-6 sm:p-8"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Close project details"
                onClick={() => setSelectedProject(null)}
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                <FaTimes />
              </button>

              <div className="pr-10">
                {selectedProject.practice && (
                  <span className="mb-3 inline-block rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500 dark:bg-zinc-800 dark:text-zinc-400">
                    Practice build
                  </span>
                )}
                <h3 className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
                  {selectedProject.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-zinc-400">
                  {selectedProject.desc}
                </p>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {selectedProject.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <p className="mb-3 text-sm font-semibold text-slate-700 dark:text-zinc-200">
                  Technology stack
                </p>
                <div className="flex flex-wrap gap-2">
                  {[...selectedProject.tech, ...selectedProject.extra].map((tech) => (
                    <span
                      key={tech}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                      {techIcons[tech] || <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />}
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
